/**
 * episode 점검 데이터의 로드 상태 — 화면은 `init` / `dispose`로 수명을 관리하고 스냅샷을 그린다.
 * 재시도는 `load`, URL 갱신은 `refreshMedia`가 맡는다.
 *
 * **두 단계다** — 목차(JSON) 하나를 받고, 그 목차가 가리키는 배열 파일들을 **병렬로** 받는다.
 * 배열은 Parquet 이라 API 를 거치지 않고 서명 URL 로 직통한다. episode 를 옮기면 이전 요청은
 * 의미가 없으므로 두 단계 모두 같은 `AbortSignal` 로 끊고 세대 번호로 늦은 응답을 버린다(규칙 §6).
 * URL 이 만료되면 목차를 다시 받아 새 URL 을 얻는다 — 갱신 전용 라우트가 없다(api.md).
 *
 * Owns:
 * - 로드 · 오류 · 준비 상태, 취소 · 세대, 만료 갱신의 중복 방지, 배열 묶음 받기.
 *
 * Does not own:
 * - 배열 디코드 — `@/shared/parquet`. 채널 조립 — `domain/inspectChannels.ts`.
 * - 재생 클럭 — c6 `useSyncedVideoTimeline`. 문구 — `inspectErrorMessage.ts`.
 */
import { readParquetColumn, type ParquetColumn } from "@/shared/parquet";
import { fetchInspectArray, getEpisodeInspect } from "../api/inspectApi";
import type { EpisodeInspect, InspectChannel } from "../domain/episodeInspect";
import { buildChannels } from "../domain/inspectChannels";
import { isInspectRetryable, resolveInspectErrorMessage } from "./inspectErrorMessage";

export type EpisodeInspectLoadState =
  | { status: "loading" }
  | { status: "error"; message: string; retryable: boolean }
  | {
      status: "ready";
      inspect: EpisodeInspect;
      channels: readonly InspectChannel[];
      refreshingMedia: boolean;
    };

export type EpisodeInspectLoaderOptions = {
  probeUuid: string;
  sourceId: string;
  /** 테스트 주입 지점. 기본은 실제 경계. */
  fetchInspect?: typeof getEpisodeInspect;
  fetchArray?: typeof fetchInspectArray;
  decodeArray?: typeof readParquetColumn;
};

export class EpisodeInspectLoader {
  private readonly options: EpisodeInspectLoaderOptions;
  private readonly fetchInspect: typeof getEpisodeInspect;
  private readonly fetchArray: typeof fetchInspectArray;
  private readonly decodeArray: typeof readParquetColumn;
  private readonly listeners = new Set<() => void>();
  private snapshot: EpisodeInspectLoadState = { status: "loading" };
  private generation = 0;
  private controller: AbortController | null = null;
  private disposed = false;

  constructor(options: EpisodeInspectLoaderOptions) {
    this.options = options;
    this.fetchInspect = options.fetchInspect ?? getEpisodeInspect;
    this.fetchArray = options.fetchArray ?? fetchInspectArray;
    this.decodeArray = options.decodeArray ?? readParquetColumn;
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot = (): EpisodeInspectLoadState => this.snapshot;

  /** StrictMode의 정리 후 재초기화도 새 요청으로 시작한다. 이전 세대의 응답은 계속 버린다. */
  async init(): Promise<void> {
    this.disposed = false;
    await this.load();
  }

  /** 처음 · 재시도 로드. 진행 중이던 요청은 끊는다. */
  async load(): Promise<void> {
    if (this.disposed) {
      return;
    }
    this.set({ status: "loading" });
    await this.request();
  }

  /**
   * 카메라 URL 만료 · 영상 오류 뒤의 재조회 — 준비 상태를 유지한 채(영상만 새 URL 로 갈린다)
   * 같은 요청을 다시 보낸다. 이미 갱신 중이면 겹치지 않는다.
   */
  async refreshMedia(): Promise<void> {
    const current = this.snapshot;
    if (this.disposed || current.status !== "ready" || current.refreshingMedia) {
      return;
    }
    this.set({ ...current, refreshingMedia: true });
    await this.request();
  }

  dispose(): void {
    this.disposed = true;
    this.generation += 1;
    this.controller?.abort();
    this.controller = null;
  }

  private async request(): Promise<void> {
    const generation = (this.generation += 1);
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    try {
      const inspect = await this.fetchInspect(
        this.options.probeUuid,
        this.options.sourceId,
        controller.signal,
      );
      if (generation !== this.generation) {
        return;
      }
      const arrays = await this.loadArrays(inspect, controller.signal);
      if (generation !== this.generation) {
        return;
      }
      this.set({
        status: "ready",
        inspect,
        channels: buildChannels(inspect, arrays),
        refreshingMedia: false,
      });
    } catch (error: unknown) {
      if (generation !== this.generation) {
        return;
      }
      const message = resolveInspectErrorMessage(error);
      const current = this.snapshot;
      // 갱신 실패는 보던 데이터를 지우지 않는다 — 영상만 못 볼 뿐 채널 표는 유효하다.
      if (current.status === "ready") {
        this.set({ ...current, refreshingMedia: false });
        return;
      }
      this.set({ status: "error", message, retryable: isInspectRetryable(error) });
    }
  }

  /** 목차가 가리키는 배열을 전부 병렬로 받는다 — 서로 독립이라 줄 세우지 않는다(규칙 §6). */
  private async loadArrays(
    inspect: EpisodeInspect,
    signal: AbortSignal,
  ): Promise<Map<string, ParquetColumn>> {
    const decoded = await Promise.all(
      inspect.artifacts
        .filter((file) => file.path.endsWith(".parquet"))
        .map(async (file) => {
          const bytes = await this.fetchArray(file.url, signal);
          return [file.path, await this.decodeArray(bytes)] as const;
        }),
    );
    return new Map(decoded);
  }

  private set(next: EpisodeInspectLoadState): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
